"use client"
import React, {useRef} from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { useGSAP } from '@gsap/react'


gsap.registerPlugin(SplitText, ScrollTrigger)

export default function TextSplit({ children, animateOnScroll = true, delay = 0, vertical = true, scroller = null }) {
  const splitContainerRef = useRef(null)
  const elementRef = useRef([])
  const splitRef = useRef([])
  const lines = useRef([])

  useGSAP(
    () => {
      requestAnimationFrame(() => {
        if (!splitContainerRef.current) return

        splitRef.current = []
        elementRef.current = []
        lines.current = []

        let elements = []

        if (splitContainerRef.current.hasAttribute("data-copy-wrapper")) {
          elements = Array.from(splitContainerRef.current.children)
        } else {
          elements = [splitContainerRef.current]
        }

        elements.forEach(element => {
          elementRef.current.push(element)

          const split = SplitText.create(element, {
            type: "lines",
            mask: "lines",
            linesClass: "line++"
          })

          splitRef.current.push(split)

          const computedStyle = window.getComputedStyle(element)
          const textIndent = computedStyle.textIndent

          if (textIndent && textIndent !== "0px") {
            if (split.lines.length > 0) {
              split.lines[0].style.paddingLeft = textIndent
            }
            element.style.textIndent = "0"
          }

          lines.current.push(...split.lines)
        })

        gsap.set(lines.current, { y: "100%", opacity: 0 })

        const animationProps = {
          y: "0%",
          duration: 1,
          stagger: 0.1,
          ease: "power4.out",
          delay: delay,
          opacity: 1
        }

        if (animateOnScroll) {
          gsap.to(lines.current, {
            ...animationProps,
            scrollTrigger: {
              trigger: splitContainerRef.current,
              start: vertical ? "top 75%" : "left 75%",
              horizontal: vertical ? false : true,
              scroller: vertical ? null : splitContainerRef.current,
              once: false,
            }
          })
          ScrollTrigger.refresh() // Add this
        } else {
          gsap.to(lines.current, animationProps)
        }

        return () => {
          splitRef.current.forEach(split => {
            if (split) {
              split.revert()
            }
          })
          ScrollTrigger.getAll().forEach(trigger => trigger.kill())
        }
      })
    },
    {
      scope: splitContainerRef,
      dependencies: [animateOnScroll, delay, vertical, scroller]
    }
  )

  if (React.Children.count(children) === 1) {
    return React.cloneElement(children, { ref: splitContainerRef })
  }

  return (
    <div ref={splitContainerRef} data-copy-wrapper="true">
      {children}
    </div>
  )
}
